<template>
  <div class="container py-5">
      
    <div class="buttons pb-3">
      <router-link
        :to="{ name: 'DeliveryMaker' }"
        class="button is-primary">
        Add a Delivery
      </router-link>
      <button 
        class="button"
        v-if="selected.length > 0"
        @click="launchConfirmDelete()">
        <span class="icon is-medium">
          <i class="fas fa-trash has-text-danger"></i>
        </span>
        <span>Delete {{ selected.length }} selected deliveries</span>
      </button>
    </div>

    <article 
      v-if="!loading && deliveries.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no deliveries.
      </div>
    </article>

    <table 
      class="table is-fullwidth is-hoverable is-clickable has-smallish-font">

      <thead>
        <tr>
          <th @click.stop="">
            <v-checkbox 
              :value="allSelected"
              @input="toggleAllSelected($event)"/>
          </th>
          <th 
            v-for="(header, headerIndex) in headers"
            :key="headerIndex">
            {{ header }}
          </th>
        </tr>
        <tr v-if="loading">
          <th :colspan="headers.length + 1" class="py-0 px-0 is-naked">
            <v-progress class="is-fullwidth" v-model="loading"/>
          </th>
        </tr>
      </thead>

      <tbody v-if="!loading && deliveries.length > 0">
        <template v-for="delivery in deliveries">
          <tr 
            @click="viewDetails(delivery)"
            class="v-centered"
            :key="delivery.id">

            <td>
              <v-checkbox 
                @click.stop=""
                :value="selected.includes(delivery.id)"
                @input="toggleSelected(delivery.id)"/>
            </td>

            <template>
              <td>
                {{ delivery.delivery_no }}
              </td>
              <td>
                {{ delivery.project.project_name }}
              </td>
              <td>
                {{ delivery.delivery_storages.length }}
              </td>
              <td class="is-size-7">
                <div>{{ delivery.address }}</div>
                <div>{{ delivery.city }}, {{ delivery.province_state }}, {{ delivery.postal_code }}</div>
              </td>
              <td>
                <div>{{ delivery.email }}</div>
              </td>
              <td>
                <div>{{ delivery.phone1 }}</div>
              </td>
              <td>
                <div class="is-size-7">
                  {{ format(parse(delivery.exp_delivery_date)) }}
                </div>
              </td>
              <td>
                <div class="is-size-7" v-if="delivery.ready_for_shipping_on">
                  {{ format(parse(delivery.ready_for_shipping_on)) }}
                </div>
                <v-datepicker-modal-slot
                  v-else
                  has-time
                  :value="delivery.ready_for_shipping_on"
                  @input="update(delivery.id, 'ready_for_shipping_on', $event.toISOString())">
                  <button
                    class="button is-small">
                    <span class="icon">
                      <i class="fas fa-plus"></i>
                    </span>
                    <span>
                      Ready for Shipping
                    </span>
                  </button>
                </v-datepicker-modal-slot>
              </td>
              <td>
                <div class="is-size-7" v-if="delivery.shipped_on">
                  {{ format(parse(delivery.shipped_on)) }}
                </div>
                <button 
                  v-else
                  @click.stop="addingShippingInfo = true; editingId = delivery.id"
                  :disabled="!delivery.ready_for_shipping_on"
                  class="button is-small">
                  <span class="icon">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>Shipped</span>
                </button>
              </td>
              <td>
                <div class="is-size-7" v-if="delivery.delivered_on">
                  {{ format(parse(delivery.delivered_on)) }}
                </div>
                <v-datepicker-modal-slot
                  v-else
                  has-time
                  :value="delivery.delivered_on"
                  @input="update(delivery.id, 'delivered_on', $event.toISOString())">
                  <button 
                    :disabled="!delivery.shipped_on" 
                    class="button is-small">
                    <span class="icon">
                      <i class="fas fa-plus"></i>
                    </span>
                    <span>Delivered</span>
                  </button>
                </v-datepicker-modal-slot>
              </td>
            </template>

            <td>
              <div class="buttons buttons-right">
                <button 
                  @click.stop="launchConfirmDelete(delivery.id)"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </button>
              </div>
            </td>

          </tr>

        </template>
        
      </tbody>
    </table>

    <add-shipping-information-modal
      v-model="addingShippingInfo"
      @save="addShippingInfo"/>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>
    
    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VDatepickerModalSlot from '@/components/base/VDatepickerModalSlot'
import VProgress from '@/components/base/VProgress'
import AddShippingInformationModal from '@/components/delivery/AddShippingInformationModal'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VCheckbox,
    VConfirmModal, 
    VPagination,
    VDatepickerModalSlot,
    VProgress,
    AddShippingInformationModal
  },
  data() {
    return {
      headers: [
        'Delivery #', 'Project', '# of Items', 'Delivery Address', 
        'Email', 'Phone', 'Exp. Delivery Date', 'Ready for Shipping On', 
        'Shipped On', 'Delivered On', ''
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this delivery?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      },
      addingShippingInfo: false,
      editingId: null
    }
  },
  computed: {
    ...mapState("deliveries", [
      "deliveries", "loading", "selected", "allSelected",
      "filters", "pagination"
    ])
  },
  watch: {
    'pagination.page'() {
      let params = {...this.pagination, ...this.filters};
      this.getDeliveries(params);
    },
    filters: {
      handler(filters) {
        this.getDeliveries(filters);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("deliveries", [
      "getDeliveries", "deleteDelivery", "updateDelivery", "addImages",
      "toggleAllSelected"
    ]),
    ...mapMutations("deliveries", [
      "toggleSelected", "changePage", "setFilter"
    ]),
    initialize() {
      this.getDeliveries();
    },
    viewDetails(delivery) {
      this.$router.push({ 
        name: 'DeliveryViewer',
        params: {
          deliveryId: delivery.id,
          delivery: delivery
        }
      })
    },
    update(id, field, value) {
      let data = {}
      data[field] = value;

      this.updateDelivery({
        deliveryId: id,
        data: data
      })
    },
    addShippingInfo(data) {
      this.updateDelivery({
        deliveryId: this.editingId,
        data: data.data
      });

      let formData = new FormData;
      data.files.forEach(file => {
        formData.append('images', file.file, file.file.name);
      });

      this.addImages({
        deliveryId: this.editingId,
        data: formData
      });
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteDelivery(id);
            this.confirmDelete.state = false;
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} deliveries?`;
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteDelivery(id);
          this.confirmDelete.state = false;
        }
        this.confirmDelete.message = 'Are you sure you want to delete this delivery?';
        this.confirmDelete.state = true;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>